import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import './App.css';

const { FiArrowRight, FiCheck, FiRefreshCw } = FiIcons;

const quizData = {
  title: "50歳からのビジネスタイプ診断！",
  description: "「もう遅いかも…」と思っていませんか？ 実は、今こそあなたの経験と強みを活かすチャンス。5つの質問に答えるだけで、あなたにピッタリの起業スタイルがわかります。結果を見たら、あなたの\"第二の人生のビジネスストーリー\"が動き出します。",
  
  questions: [
    {
      id: 1,
      text: "あなたの\"起業の一歩目\"を踏み出す時に重視するのは？",
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&h=300&fit=crop",
      options: [
        { text: "まずはスピード感を大切に行動", type: "D" },
        { text: "周囲との調和や信頼関係", type: "C" },
        { text: "自分ならではのアイデア", type: "B" },
        { text: "事前準備や情報収集", type: "A" }
      ]
    },
    {
      id: 2,
      text: "「自由時間」が1日あったら、何をしたい？",
      image: "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=600&h=300&fit=crop",
      options: [
        { text: "一人で集中して取り組む", type: "A" },
        { text: "仲間と交流して過ごす", type: "C" },
        { text: "新しい知識をインプットする", type: "B" },
        { text: "スキルを磨く学習に使う", type: "D" }
      ]
    },
    {
      id: 3,
      text: "あなたの強みを最も活かせるビジネスツールは？",
      image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=600&h=300&fit=crop",
      options: [
        { text: "チャットやコミュニケーション", type: "C" },
        { text: "数字やデータの分析", type: "A" },
        { text: "デザインや発信ツール", type: "B" },
        { text: "タスク管理やプロジェクト管理", type: "D" }
      ]
    },
    {
      id: 4,
      text: "人からの感謝で一番うれしいのは？",
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=300&fit=crop",
      options: [
        { text: "「頼りにしてるよ」", type: "D" },
        { text: "「助かった、ありがとう！」", type: "C" },
        { text: "「そのアイデア面白い！」", type: "B" },
        { text: "「分析がさすがだね！」", type: "A" }
      ]
    },
    {
      id: 5,
      text: "あなたが\"これからの仕事\"で求めるものは？",
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&h=300&fit=crop",
      options: [
        { text: "安定感", type: "A" },
        { text: "新しい挑戦", type: "D" },
        { text: "仲間との一体感", type: "C" },
        { text: "自由な発想", type: "B" }
      ]
    }
  ],

  results: {
    A: {
      type: "仕組み設計型",
      description: "緻密な計画と分析で成功への道筋を作る「戦略家」。ビジネスの土台を固め、安定した収益を生み出せます。",
      url: "https://www.digishift.co.jp/40start-a",
      percentage: 28,
      color: "from-blue-500 to-blue-700"
    },
    B: {
      type: "発信クリエイター型", 
      description: "アイデアや情熱を形にして発信する「クリエイター」。人を惹きつけ、新しい価値を世に広めます。",
      url: "https://www.digishift.co.jp/40start-b",
      percentage: 24,
      color: "from-purple-500 to-purple-700"
    },
    C: {
      type: "共感サポーター型",
      description: "人を支え、人に寄り添う「サポーター」。信頼関係を築き、仲間や顧客から愛される起業家になれます。",
      url: "https://www.digishift.co.jp/40start-c", 
      percentage: 31,
      color: "from-green-500 to-green-700"
    },
    D: {
      type: "実践チャレンジャー型",
      description: "行動力と挑戦心で未来を切り開く「チャレンジャー」。スピード感を武器に、新しい市場を開拓します。",
      url: "https://www.digishift.co.jp/40start-d",
      percentage: 17,
      color: "from-orange-500 to-orange-700"
    }
  }
};

function App() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [isStarted, setIsStarted] = useState(false);

  const handleAnswer = (selectedOption) => {
    const newAnswers = [...answers, selectedOption.type];
    setAnswers(newAnswers);

    if (currentQuestion < quizData.questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestion(currentQuestion + 1);
      }, 500);
    } else {
      setTimeout(() => {
        setShowResult(true);
      }, 500);
    }
  };

  const calculateResult = () => {
    const counts = { A: 0, B: 0, C: 0, D: 0 };
    answers.forEach(answer => counts[answer]++);
    return Object.keys(counts).reduce((a, b) => counts[a] > counts[b] ? a : b);
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResult(false);
    setIsStarted(false);
  };

  const startQuiz = () => {
    setIsStarted(true);
  };

  if (!isStarted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto text-center"
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold text-white mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              {quizData.title}
            </motion.h1>
            
            <motion.p 
              className="text-lg text-white/90 mb-8 leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              {quizData.description}
            </motion.p>

            <motion.button
              onClick={startQuiz}
              className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-bold py-4 px-8 rounded-full text-lg hover:from-yellow-500 hover:to-orange-600 transition-all duration-300 transform hover:scale-105 shadow-lg"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              診断をスタート
              <SafeIcon icon={FiArrowRight} className="ml-2 inline" />
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (showResult) {
    const resultType = calculateResult();
    const result = quizData.results[resultType];

    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl mx-auto text-center"
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="mb-6">
                <SafeIcon icon={FiCheck} className="text-6xl text-green-400 mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-white mb-2">診断完了！</h2>
                <p className="text-white/80">あなたのビジネスタイプは...</p>
              </div>

              <div className={`bg-gradient-to-r ${result.color} rounded-2xl p-6 mb-6`}>
                <h3 className="text-2xl font-bold text-white mb-4">
                  {resultType}タイプ: {result.type}
                </h3>
                <p className="text-white/90 text-lg leading-relaxed">
                  {result.description}
                </p>
              </div>

              <div className="bg-white/5 rounded-xl p-4 mb-6">
                <p className="text-white/80 text-sm">
                  あなたと同じタイプは全体の<span className="font-bold text-yellow-400 text-lg">{result.percentage}%</span>です
                </p>
              </div>

              <motion.a
                href={result.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-bold py-4 px-8 rounded-full text-lg hover:from-yellow-500 hover:to-orange-600 transition-all duration-300 transform hover:scale-105 shadow-lg mb-4"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ▶ あなたの未来を変える第一歩はこちら
              </motion.a>

              <div className="mt-6">
                <button
                  onClick={resetQuiz}
                  className="text-white/60 hover:text-white transition-colors duration-300 flex items-center justify-center mx-auto"
                >
                  <SafeIcon icon={FiRefreshCw} className="mr-2" />
                  もう一度診断する
                </button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    );
  }

  const question = quizData.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / quizData.questions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="max-w-2xl mx-auto w-full"
      >
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-white/80 text-sm">質問 {currentQuestion + 1} / {quizData.questions.length}</span>
              <span className="text-white/80 text-sm">{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-2">
              <motion.div
                className="bg-gradient-to-r from-yellow-400 to-orange-500 h-2 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestion}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
            >
              {/* Question Image */}
              <div className="mb-6">
                <img
                  src={question.image}
                  alt="Question illustration"
                  className="w-full h-48 object-cover rounded-xl"
                />
              </div>

              {/* Question Text */}
              <h2 className="text-2xl font-bold text-white mb-8 text-center">
                {question.text}
              </h2>

              {/* Options */}
              <div className="space-y-4">
                {question.options.map((option, index) => (
                  <motion.button
                    key={index}
                    onClick={() => handleAnswer(option)}
                    className="w-full p-4 bg-white/10 hover:bg-white/20 rounded-xl text-white text-left transition-all duration-300 border border-white/20 hover:border-white/40"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="font-medium">{option.text}</span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}

export default App;